import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { 
  ArrowLeft,
  Clock,
  Eye,
  Share2,
  Bookmark,
  Download,
  Video,
  FileText,
  CheckSquare,
  Calendar,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const ResourceDetailPage = () => {
  const { isDark } = useTheme();
  const { getLogo } = useAssetContext();
  const { resourceId } = useParams();
  const navigate = useNavigate();
  
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedResources, setRelatedResources] = useState([]);

  useEffect(() => {
    const fetchResource = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/resources/${resourceId}`);
        if (res.ok) {
          const data = await res.json();
          setResource(data);
          
          // Fetch related resources in same category
          if (data.category_id) {
            const relatedRes = await fetch(`${API_URL}/api/resources?category=${data.category_id}&limit=4`);
            if (relatedRes.ok) {
              const relatedData = await relatedRes.json();
              setRelatedResources(
                relatedData.resources?.filter(r => r.resource_id !== data.resource_id).slice(0, 3) || []
              );
            }
          }
        } else {
          toast.error('Resource not found');
          navigate('/uac-desk/resources');
        }
      } catch (error) {
        console.error('Error fetching resource:', error);
        toast.error('Failed to load resource');
      } finally {
        setLoading(false);
      }
    };

    if (resourceId) {
      fetchResource();
    }
  }, [resourceId, navigate]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: resource.title,
          text: resource.description,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard');
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="h-12 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!resource) {
    return null;
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1a2830]' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/uac-desk/resources">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                Back to Resources
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleShare}>
                <Share2 className="w-4 h-4" />
              </Button>
              <Link to="/">
                <img src={getLogo()} alt="UniXperts" className={`h-8 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumb */}
        <nav className={`text-sm mb-6 flex items-center gap-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          <Link to="/uac-desk/resources" className="hover:text-ux-cyan">Resources</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/uac-desk/resources?category=${resource.category_id}`} className="hover:text-ux-cyan capitalize">
            {resource.category_id?.replace(/-/g, ' ')}
          </Link>
        </nav>

        {/* Title & Meta */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Badge className={`capitalize ${
              resource.content_type === 'video' 
                ? 'bg-rose-100 text-rose-700' 
                : resource.content_type === 'pdf'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-blue-100 text-blue-700'
            }`}>
              {resource.content_type === 'video' && <Video className="w-3 h-3 mr-1" />}
              {resource.content_type === 'pdf' && <Download className="w-3 h-3 mr-1" />}
              {resource.content_type === 'article' && <FileText className="w-3 h-3 mr-1" />}
              {resource.content_type === 'checklist' && <CheckSquare className="w-3 h-3 mr-1" />}
              {resource.content_type}
            </Badge>
            {resource.country && (
              <Badge variant="outline">{resource.country}</Badge>
            )}
            {resource.is_featured && (
              <Badge className="bg-ux-violet/10 text-ux-violet">Featured</Badge>
            )}
          </div>
          
          <h1 className={`text-2xl sm:text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            {resource.title}
          </h1>
          
          <p className={`text-lg ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            {resource.description}
          </p>
          
          <div className={`flex flex-wrap items-center gap-4 mt-4 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            {resource.reading_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {resource.reading_time} min read
              </span>
            )}
            {resource.views > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {resource.views} views
              </span>
            )}
            {resource.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(resource.created_at)}
              </span>
            )}
          </div>
        </div>

        {/* Tags */}
        {resource.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {resource.tags.map((tag, i) => (
              <Link 
                key={i}
                to={`/uac-desk/resources?search=${tag}`}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  isDark 
                    ? 'bg-white/10 text-white/70 hover:bg-white/20' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}

        {/* Content */}
        <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
          {/* Video Embed */}
          {resource.content_type === 'video' && resource.video_url && (
            <div className="aspect-video">
              <iframe
                src={resource.video_url}
                title={resource.title}
                className="w-full h-full"
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )}

          {/* PDF Download */}
          {resource.content_type === 'pdf' && resource.file_url && (
            <div className={`p-8 text-center ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <Download className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Download Resource
              </h3>
              <p className={`mb-4 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                Click below to download this resource as a PDF
              </p>
              <a href={resource.file_url} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-ux-cyan to-ux-blue">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </a>
            </div>
          )}

          {/* Article Content */}
          {(resource.content_type === 'article' || resource.content_type === 'checklist') && resource.content && (
            <div className="p-6 sm:p-8">
              <div 
                className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
                dangerouslySetInnerHTML={{ __html: resource.content }}
              />
            </div>
          )}

          {/* Placeholder for empty content */}
          {!resource.content && !resource.video_url && !resource.file_url && (
            <div className={`p-8 text-center ${isDark ? '' : 'bg-gray-50'}`}>
              <FileText className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`} />
              <p className={isDark ? 'text-white/60' : 'text-gray-600'}>
                Content coming soon...
              </p>
            </div>
          )}
        </div>

        {/* Related Resources */}
        {relatedResources.length > 0 && (
          <section className="mt-12">
            <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
              Related Resources
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {relatedResources.map((related) => (
                <Link 
                  key={related.resource_id}
                  to={`/uac-desk/resources/${related.slug || related.resource_id}`}
                  className={`p-4 rounded-xl transition-all hover:scale-[1.02] ${
                    isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-white border border-gray-200 hover:shadow-md'
                  }`}
                >
                  <Badge variant="outline" className="text-xs mb-2 capitalize">
                    {related.content_type}
                  </Badge>
                  <h3 className={`font-semibold line-clamp-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                    {related.title}
                  </h3>
                  <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                    {related.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className={`mt-12 p-6 rounded-2xl text-center ${isDark ? 'bg-gradient-to-r from-ux-cyan/20 to-ux-blue/20' : 'bg-gradient-to-r from-cyan-50 to-blue-50'}`}>
          <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
            Ready to Start Your Journey?
          </h3>
          <p className={`mb-4 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
            Use our AI-powered tools to find your perfect university match
          </p>
          <Link to="/uac-desk/ai-finder">
            <Button className="bg-gradient-to-r from-ux-cyan to-ux-blue">
              Find Universities
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default ResourceDetailPage;
